/**
 * Blogs service. Firestore-backed; signatures unchanged from mock mode.
 */
import { mockBlogs, mockBlogCategories } from "@/data/mock-blogs";
import { USE_MOCK_DATA } from "@/lib/firebase";
import { getPublished, getPublishedBySlug, getAll } from "@/lib/firestore-data";

const delay = (ms = 60) => new Promise((res) => setTimeout(res, ms));

async function source() {
    if (USE_MOCK_DATA) {
        await delay();
        return mockBlogs;
    }
    return getPublished("blogs");
}

export async function getBlogPosts(filters = {}) {
    const { search = "", category = "", tag = "", page = 1, pageSize = 6 } = filters;

    let items = (await source()).filter((b) => b.status === "published");

    if (search) {
        const q = search.toLowerCase();
        items = items.filter(
            (b) =>
                b.title.toLowerCase().includes(q) ||
                b.excerpt.toLowerCase().includes(q) ||
                (b.tags || []).some((t) => t.toLowerCase().includes(q)),
        );
    }
    if (category) items = items.filter((b) => b.categorySlug === category);
    if (tag) items = items.filter((b) => (b.tags || []).includes(tag));

    items = items.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

    const totalItems = items.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const start = (page - 1) * pageSize;

    return {
        items: items.slice(start, start + pageSize),
        page,
        pageSize,
        totalItems,
        totalPages,
    };
}

export async function getFeaturedBlogPost() {
    const posts = (await source()).filter((b) => b.status === "published");
    return posts.find((b) => b.featured) || posts[0] || null;
}

export async function getBlogPostBySlug(slug) {
    if (USE_MOCK_DATA) {
        await delay();
        return mockBlogs.find((b) => b.slug === slug && b.status === "published") || null;
    }
    return getPublishedBySlug("blogs", slug);
}

export async function getRelatedBlogPosts(postId, limit = 3) {
    const posts = await source();
    const origin = posts.find((b) => b.id === postId);
    if (!origin) return [];

    return posts
        .filter((b) => b.id !== postId && b.status === "published")
        .map((b) => ({
            b,
            score:
                (b.categorySlug === origin.categorySlug ? 2 : 0) +
                (b.tags || []).filter((t) => (origin.tags || []).includes(t)).length,
        }))
        .sort((x, y) => y.score - x.score)
        .slice(0, limit)
        .map((x) => x.b);
}

export async function getBlogCategories() {
    if (USE_MOCK_DATA) {
        await delay();
        return mockBlogCategories;
    }
    // Taxonomy has no draft state, so this reads the whole (small) collection
    // rather than filtering on status. firestore.rules allows a public list here.
    return getAll("blogCategories");
}
